import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Alert,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Backdrop,
  Slide,
  Fade,
  Fab,
  Skeleton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Search,
  ExpandMore,
  FilterAlt,
  ClearAll,
  History as HistoryIcon,
  Close,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QuoteHistoryItem, formatQuoteDate } from "./quoteHistory";
import { useQuoteHistory } from "./useQuoteHistory";

interface QuoteHistorySidebarProps {
  currentRfqId?: number;
  isMinimized?: boolean;
  onToggleMinimized?: () => void;
}

// Component to display a single quote in compact format
function CompactQuote({ item, isCurrentRfq }: { item: QuoteHistoryItem, isCurrentRfq?: boolean }) {
  const formattedDate = formatQuoteDate(item);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        border: 1,
        borderColor: (theme) => (isCurrentRfq ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider),
        borderRadius: 1,
        bgcolor: (theme) => (isCurrentRfq ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper),
        '&:before': { display: 'none' },
        '&:first-of-type, &:last-of-type': { borderRadius: 1 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ minHeight: 48, px: 1.5, '& .MuiAccordionSummary-content': { my: 1 } }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              RFQ #{item.rfq_id}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formattedDate}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" noWrap sx={{ fontSize: '0.8125rem' }}>
            {item.customer_name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
              Items: {item.items_count}
            </Typography>
            <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              ${(item.total_amount ?? 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>Item</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>Price</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>Days</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(item.items ?? [])
                .slice()
                .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id))
                .slice(0, 5)
                .map((rfqItem) => (
                <TableRow key={rfqItem.rfq_item_id}>
                  <TableCell sx={{ fontSize: '0.8125rem', px: 1, py: 0.5, maxWidth: 0, width: '100%' }}>
                    <Tooltip title={`${rfqItem.item_name} - ${rfqItem.model_number}`}>
                      <Typography sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rfqItem.item_name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>{rfqItem.quantity}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>${(rfqItem.quoted_price ?? 0).toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8125rem', px: 1, py: 0.5 }}>{rfqItem.indent_days}</TableCell>
                </TableRow>
              ))}
              {(item.items?.length ?? 0) > 5 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontSize: '0.8125rem', px: 1, py: 0.5, textAlign: 'center', color: 'text.secondary' }}>
                    +{(item.items?.length ?? 0) - 5} more items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Minimized: a floating button docked bottom-left, the mirror of the AI
 * Analyst FAB bottom-right (ChatPanel: Fab, bottom/right 24, zIndex 1200).
 * Docked there it never covers the page title or a table, and every Page
 * reserves bottom padding so the last row scrolls clear of it.
 */
function MinimizedHistoryButton({ hasCurrentRfqHistory, onClick }: { hasCurrentRfqHistory: boolean | undefined; onClick: () => void }) {
  return (
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Quote History</Typography>
          <Typography variant="caption">
            {hasCurrentRfqHistory ? 'History available for this RFQ' : 'View all quote history'}
          </Typography>
        </Box>
      } 
      placement="right"
    >
      <Fab
        color="primary"
        aria-label="Quote history"
        onClick={onClick}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 1200,
          boxShadow: 4,
        }}
      >
        <HistoryIcon />
        {/* Indicator dot if current RFQ has history */}
        {hasCurrentRfqHistory && (
          <Fade in timeout={600}>
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 14,
                height: 14,
                bgcolor: 'success.main',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 16px rgba(76, 175, 80, 0.6)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                  },
                },
              }}
            />
          </Fade>
        )}
      </Fab>
    </Tooltip>
  );
}

/** Customer, RFQ id and date filters, with Search and Clear. */
function AdvancedFilters({ filters, onSearch }: { filters: ReturnType<typeof useQuoteHistory>; onSearch: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Customer Email"
            value={filters.customerEmail}
            onChange={(e) => filters.setCustomerEmail(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="RFQ ID"
            type="number"
            value={filters.rfqId}
            onChange={(e) => filters.setRfqId(e.target.value ? Number(e.target.value) : "")}
          />
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => filters.setStartDate(date)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => filters.setEndDate(date)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Search />}
            onClick={onSearch}
            fullWidth
          >
            Search
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearAll />}
            onClick={filters.clearFilters}
            fullWidth
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

// Main Quote History Sidebar Component
export default function QuoteHistorySidebar({ 
  currentRfqId, 
  isMinimized = true, 
  onToggleMinimized 
}: QuoteHistorySidebarProps) {
  const filters = useQuoteHistory({
    order: "desc", // Largest RFQ id first
    query: {
      staleTime: 60000, // Cache results for 60 seconds - increased for better performance
      gcTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      refetchOnMount: false, // Don't refetch if data is still fresh
      enabled: !!currentRfqId, // Only run when there's an active RFQ
    },
  });
  const { keyword, setKeyword, data, isLoading, error, refetch, quotes } = filters;
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Handle toggle
  const handleToggle = () => {
    if (onToggleMinimized) {
      onToggleMinimized();
    }
  };

  if (isMinimized) {
    const hasCurrentRfqHistory = data?.history?.some(item => item.rfq_id === currentRfqId);
    
    return <MinimizedHistoryButton hasCurrentRfqHistory={hasCurrentRfqHistory} onClick={handleToggle} />;
  }

  return (
    <>
      {/* Backdrop */}
      <Backdrop
        open={!isMinimized}
        onClick={handleToggle}
        sx={{ 
          zIndex: 999,
          bgcolor: 'rgba(0, 0, 0, 0.3)',
        }}
      />
      
      {/* Sliding Panel */}
      <Slide
        direction="right"
        in={!isMinimized}
        timeout={{ enter: 200, exit: 150 }}
        easing={{ enter: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', exit: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)' }}
      >
        <Box>
          <Card 
          sx={{ 
            width: { xs: '95vw', sm: '85vw', md: '75vw', lg: 600, xl: 720 },
            height: { xs: '85vh', lg: '90vh' },
            position: 'fixed',
            top: { xs: '7.5vh', lg: '5vh' },
            left: { xs: '2.5vw', sm: '7.5vw', md: '12.5vw', lg: 24, xl: 32 },
            zIndex: 1000,
            bgcolor: 'background.paper',
            boxShadow: '0 16px 40px rgba(16, 24, 40, 0.18), 0 2px 6px rgba(16, 24, 40, 0.08)',
            borderRadius: 1.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: 1,
            borderColor: 'divider',
          }}
          onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking inside
        >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <HistoryIcon sx={{ fontSize: 22, color: 'primary.main' }} />
            <Typography variant="h6" component="h2">
              Quote History
            </Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={filtersExpanded ? "Hide Filters" : "Show Advanced Filters"} placement="left">
              <IconButton
                size="small"
                aria-label={filtersExpanded ? "Hide filters" : "Show advanced filters"}
                aria-expanded={filtersExpanded}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                sx={{
                  color: filtersExpanded ? 'primary.contrastText' : 'primary.main',
                  bgcolor: filtersExpanded ? 'primary.main' : 'transparent',
                  '&:hover': {
                    bgcolor: (theme) => (filtersExpanded ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08)),
                  }
                }}
              >
                <FilterAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" placement="left">
              <IconButton
                size="small"
                aria-label="Close quote history"
                onClick={handleToggle}
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          py: 1.5,
          px: { xs: 2, sm: 3 },
          '& .MuiCardHeader-action': { alignSelf: 'center', m: 0 },
        }}
      />
      
      <CardContent sx={{ pt: 2, pb: 1, px: { xs: 2, sm: 3 }, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Quick search */}
        <TextField
          fullWidth
          size="small"
          label="Quick Search"
          placeholder="Search items, customer, RFQ ID..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: keyword && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setKeyword("")}
                  edge="end"
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, mt: 0.5 }}
        />

        {/* Advanced Filters */}
        <Collapse in={filtersExpanded}>
          <AdvancedFilters filters={filters} onSearch={() => refetch()} />
        </Collapse>

        {/* Results count */}
        {!isLoading && data?.history && (
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
            {data.history.length} {data.history.length === 1 ? 'Quote' : 'Quotes'} Found
          </Typography>
        )}

        {/* Results Section */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mx: -0.5, px: 0.5 }}>
          {isLoading && (
            <Box sx={{ p: 2 }}>
              {/* Skeleton loading for better perceived performance */}
              {[...Array(3)].map((_, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="rectangular" width={60} height={20} />
                  </Box>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Skeleton variant="text" width="30%" height={16} />
                    <Skeleton variant="text" width="25%" height={16} />
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading quote history.
            </Alert>
          )}

          {!isLoading && !error && data?.history && (
            data.history.length === 0 ? (
              <Alert severity="info">
                No quote history found.
              </Alert>
            ) : (
              quotes.map((item) => (
                <CompactQuote
                  key={item.rfq_id}
                  item={item}
                  isCurrentRfq={item.rfq_id === currentRfqId}
                />
              ))
            )
          )}
        </Box>
      </CardContent>
    </Card>
        </Box>
      </Slide>
    </>
  );
} 